import { logger } from './logger.js';

export const STORAGE_KEYS = Object.freeze({
  DASHBOARD_TAB: 'dashboardActiveTab',
  REGISTER_LOCATION: 'storeRegisterLocation',
  REGISTER_DETAILS: 'storeRegisterDetails'
});

const STORAGE_MODULE = 'storage';
const LEGACY_WEB_AUTH_STORAGE_KEYS = Object.freeze(['zopeo_auth', 'zopeo_store_token']);

const isStorageAvailable = () => typeof window !== 'undefined' && window.localStorage;

const resolveKey = (key) => {
  if (!Object.prototype.hasOwnProperty.call(STORAGE_KEYS, key)) {
    logger.warn(STORAGE_MODULE, 'Unsupported storage key', { key });
    return null;
  }
  return STORAGE_KEYS[key];
};

const read = (key) => {
  const storageKey = resolveKey(key);
  if (!storageKey || !isStorageAvailable()) {
    return null;
  }
  try {
    const rawValue = window.localStorage.getItem(storageKey);
    if (rawValue === null) {
      return null;
    }
    return JSON.parse(rawValue);
  } catch (error) {
    logger.warn(STORAGE_MODULE, 'Failed to parse storage value', {
      key: storageKey,
      error: error instanceof Error ? error.message : String(error)
    });
    return null;
  }
};

const write = (key, value) => {
  const storageKey = resolveKey(key);
  if (!storageKey || !isStorageAvailable()) {
    return;
  }
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(value));
  } catch (error) {
    logger.error(STORAGE_MODULE, 'Failed to write storage value', {
      key: storageKey,
      error: error instanceof Error ? error.message : String(error)
    });
  }
};

const remove = (key) => {
  const storageKey = resolveKey(key);
  if (!storageKey || !isStorageAvailable()) {
    return;
  }
  try {
    window.localStorage.removeItem(storageKey);
  } catch (error) {
    logger.error(STORAGE_MODULE, 'Failed to remove storage value', {
      key: storageKey,
      error: error instanceof Error ? error.message : String(error)
    });
  }
};

const clear = () => {
  if (!isStorageAvailable()) {
    return;
  }
  try {
    Object.values(STORAGE_KEYS).forEach((storageKey) => {
      window.localStorage.removeItem(storageKey);
    });
  } catch (error) {
    logger.error(STORAGE_MODULE, 'Failed to clear storage values', {
      error: error instanceof Error ? error.message : String(error)
    });
  }
};

const purgeLegacyWebAuthStorage = () => {
  if (!isStorageAvailable()) {
    return;
  }
  try {
    LEGACY_WEB_AUTH_STORAGE_KEYS.forEach((storageKey) => {
      window.localStorage.removeItem(storageKey);
    });
  } catch (error) {
    logger.error(STORAGE_MODULE, 'Failed to purge legacy web auth storage', {
      error: error instanceof Error ? error.message : String(error)
    });
  }
};

export const storage = {
  read,
  write,
  remove,
  clear,
  purgeLegacyWebAuthStorage
};

export default storage;
